import { FeatureFlags } from "@observe-tech/utils";
import type { Permission, User } from "@observe-tech/mfe-shell-api";
import type { User as TideUser, UserPermissions } from "../types/user";
import { getShellSession } from "./shellSession";

export type EmergencyRole = "emergency" | "emergency-admin";

export interface EmergencyUserDefinition {
  role: EmergencyRole;
  username: string;
  password: string;
  displayName: string;
  permissions: readonly string[];
}

export const EMERGENCY_USER: EmergencyUserDefinition = {
  role: "emergency",
  username: "emergency",
  password: "Emergency-7419",
  displayName: "Emergency user",
  permissions: ["VIDEO_PAGE"],
};

export const EMERGENCY_ADMIN: EmergencyUserDefinition = {
  ...EMERGENCY_USER,
  role: "emergency-admin",
  username: "emergency-admin",
  password: "EmergencyAdmin-7419",
  displayName: "Emergency admin",
  permissions: [...EMERGENCY_USER.permissions, "SUPPORT_PAGES"],
};

const DEFINITIONS: Record<EmergencyRole, EmergencyUserDefinition> = {
  emergency: EMERGENCY_USER,
  "emergency-admin": EMERGENCY_ADMIN,
};

export const EMERGENCY_ACCOUNTS: readonly EmergencyUserDefinition[] =
  Object.values(DEFINITIONS);

export function emergencyUserDefinition(
  role: EmergencyRole,
): EmergencyUserDefinition {
  return DEFINITIONS[role];
}

const MARKER_KEY = "emergency_session";

const featureFlags = new FeatureFlags();

function isEnabled(): boolean {
  return (
    featureFlags.isFeatureEnabled("FEATUREFLAG_EMERGENCY_USERS") &&
    !!window.electronAPI
  );
}

export function isEmergencyRole(value: unknown): value is EmergencyRole {
  return EMERGENCY_ACCOUNTS.some((account) => account.role === value);
}

export function matchEmergencyAccount(
  username: string,
  password: string,
): EmergencyRole | null {
  if (!isEnabled()) return null;
  const account = EMERGENCY_ACCOUNTS.find(
    (candidate) =>
      candidate.username === username && candidate.password === password,
  );
  return account?.role ?? null;
}

export function setEmergencyMarker(role: EmergencyRole): void {
  try {
    localStorage.setItem(MARKER_KEY, role);
  } catch {
    /* storage may be disabled */
  }
}

export function clearEmergencyMarker(): void {
  try {
    localStorage.removeItem(MARKER_KEY);
  } catch {
    /* ignore */
  }
}

export function getEmergencyRole(): EmergencyRole | null {
  if (!isEnabled()) return null;
  try {
    const value = localStorage.getItem(MARKER_KEY);
    return isEmergencyRole(value) ? value : null;
  } catch {
    return null;
  }
}

export function isEmergencySession(): boolean {
  return getEmergencyRole() !== null;
}

export function emergencyPermissions(
  role: EmergencyRole,
  siteIds: number[],
): Permission[] {
  return emergencyUserDefinition(role).permissions.map((name) => ({
    name,
    active: true,
    sites: [...siteIds],
  }));
}

export function emergencyUser(role: EmergencyRole): User {
  return {
    id: role,
    email: "",
    name: emergencyUserDefinition(role).displayName,
    cognitoSub: role,
  };
}

export function currentEmergencyUser(): TideUser | undefined {
  const role = getEmergencyRole();
  if (!role) return undefined;
  const permissions =
    getShellSession()?.permissions?.raw() ?? emergencyPermissions(role, []);
  return {
    groups: [],
    email: "",
    username: emergencyUserDefinition(role).displayName,
    isManager: false,
    sites: [...new Set(permissions.flatMap((permission) => permission.sites))],
    permissions: Object.fromEntries(
      permissions.map(({ name, active, sites }) => [name, { active, sites }]),
    ) as UserPermissions,
    isExpired: false,
  };
}
